import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Tabs } from '@heroui/react';
import { countParam } from '@/pages/catalogImport';
import { getNodeTestIdProp } from '@/utils';
import {
    AppliedFilterTags,
    BarcodeIssues,
    ProductDraftsNotice,
    ProductExport,
    ProductFiltersDrawer,
    ProductTable,
    ProductsToolbar,
} from './components';
import type { ProductExportScope } from './components/productExport/useProductExport';
import type { ProductFilterGroup } from './components/productListFilters';
import type { ProductOriginState } from './components/productOrigin';
import type {
    AppliedFilterTag,
    NamedOption,
} from './components/productFilters/productFiltersMeta';
import type { CategoryFilterOption, Product } from './components/productsMeta';
import type {
    ProductBranchScope,
    StockFiguresScope,
} from './components/productFilters/branchScopeMeta';
import type { FilterOptionsState } from './components/productFilters/useFilterOptionsState';
import type { ProductFiltersDrawerState } from './useProductFiltersDrawer';
import type { ProductsView } from './useProductsPage';

type ProductsPageViewProps = {
    /** The tab shown: the catalog or Barcode issues (bos-app#239). */
    view: ProductsView;
    onViewChange: (view: ProductsView) => void;
    /** The Barcode issues tab's count; `null` until it arrives. */
    barcodeIssuesCount: number | null;
    /** The branch list still checks the address's branch. */
    isBranchPending: boolean;
    /** `null` until the first page arrives; only the grid waits for it. */
    data: Product[] | null;
    isFetching: boolean;
    search: string;
    onSearchChange: (value: string) => void;
    onCreate: () => void;
    categoryOptions: FilterOptionsState<CategoryFilterOption>;
    brandOptions: FilterOptionsState<NamedOption>;
    branchScope: ProductBranchScope;
    /** Whose stock the figures count, for the Cost header's tooltip. */
    stockFigures: StockFiguresScope;
    /** The branch the list asks for, which each product link carries. */
    branchId: string | null;
    /** The router state each product link records: this entry as its origin. */
    productLinkState: ProductOriginState | undefined;
    filtersDrawer: ProductFiltersDrawerState;
    /** What Export Excel writes out (bos-app#120). */
    exportScope: ProductExportScope;
    filterCount: number;
    appliedFilterTags: AppliedFilterTag[];
    onRemoveFilter: (group: ProductFilterGroup) => void;
    hasActiveFilters: boolean;
    onResetFilters: () => void;
    /** The list shows drafts only (bos-app#226). */
    isDraftsOnly: boolean;
    /** The drafts notice's Show drafts. */
    onShowDrafts: () => void;
    page: number;
    pageSize: number;
    totalCount: number;
    /** What the result is out of while a search or filter narrows it. */
    catalogCount: number | null;
    totalPages: number;
    pageWindow: (number | 'ellipsis')[];
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
};

const VIEWS: readonly ProductsView[] = ['catalog', 'barcodeIssues'];

/**
 * The Products page's two tabs (bos-app#239): the catalog, and Barcode
 * issues with its count, which repairs the barcodes a scanner can't use for
 * the same branch. Only the shown tab's panel is mounted.
 *
 * The Products list: the drafts notice while the catalog holds drafts
 * (bos-app#226), the toolbar with Export Excel (bos-app#120), the
 * applied-filter tags, the grid, and the filters drawer that holds every
 * filter but the search (BOS-199).
 *
 * The toolbar, the tags and the drawer work from the first render; only the
 * grid's rows show placeholders until the first page arrives, under the
 * grid's real headers, and the result count with them.
 */
export const ProductsPageView: React.FC<ProductsPageViewProps> = ({
    view,
    onViewChange,
    barcodeIssuesCount,
    isBranchPending,
    data,
    isFetching,
    search,
    onSearchChange,
    onCreate,
    categoryOptions,
    brandOptions,
    branchScope,
    stockFigures,
    branchId,
    productLinkState,
    filtersDrawer,
    exportScope,
    filterCount,
    appliedFilterTags,
    onRemoveFilter,
    hasActiveFilters,
    onResetFilters,
    isDraftsOnly,
    onShowDrafts,
    page,
    pageSize,
    totalCount,
    catalogCount,
    totalPages,
    pageWindow,
    onPageChange,
    onPageSizeChange,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Tabs
            className="w-full"
            onSelectionChange={(key) => onViewChange(key as ProductsView)}
            selectedKey={view}
            variant="secondary"
        >
            <Tabs.ListContainer className="min-w-0 max-w-max">
                <Tabs.List aria-label={t('products.barcodeIssues.tabsAria')}>
                    {VIEWS.map((id) => (
                        <Tabs.Tab
                            className="gap-2 whitespace-nowrap"
                            id={id}
                            key={id}
                            {...getNodeTestIdProp(`products-tab-${id}`)}
                        >
                            {id === 'catalog'
                                ? t('products.barcodeIssues.catalogTab')
                                : t('products.barcodeIssues.tab')}
                            {id === 'barcodeIssues' &&
                            barcodeIssuesCount !== null ? (
                                <Chip
                                    color={
                                        barcodeIssuesCount > 0
                                            ? 'warning'
                                            : 'default'
                                    }
                                    size="sm"
                                    variant="soft"
                                    {...getNodeTestIdProp(
                                        'products-tab-barcode-issues-count',
                                    )}
                                >
                                    {countParam(barcodeIssuesCount)}
                                </Chip>
                            ) : null}
                            <Tabs.Indicator />
                        </Tabs.Tab>
                    ))}
                </Tabs.List>
            </Tabs.ListContainer>
            <Tabs.Panel className="pt-4" id="catalog">
                <section
                    aria-busy={isFetching}
                    aria-label={t('products.components.productTable.tableAria')}
                    {...getNodeTestIdProp('product-table-section')}
                >
                    <div className="mb-4 flex flex-col gap-3">
                        <ProductDraftsNotice
                            isDraftsOnly={isDraftsOnly}
                            onShowDrafts={onShowDrafts}
                        />
                        <ProductsToolbar
                            catalogCount={catalogCount}
                            filterCount={filterCount}
                            onCreate={onCreate}
                            onOpenFilters={filtersDrawer.onOpen}
                            exportAction={<ProductExport scope={exportScope} />}
                            onSearchChange={onSearchChange}
                            search={search}
                            totalCount={data === null ? null : totalCount}
                        />
                        <AppliedFilterTags
                            onClearAll={onResetFilters}
                            onRemove={onRemoveFilter}
                            tags={appliedFilterTags}
                        />
                    </div>
                    <ProductTable
                        branchId={branchId}
                        data={data}
                        isFiltered={hasActiveFilters}
                        onClearFilters={onResetFilters}
                        onPageChange={onPageChange}
                        onPageSizeChange={onPageSizeChange}
                        page={page}
                        pageSize={pageSize}
                        pageWindow={pageWindow}
                        productLinkState={productLinkState}
                        stockFigures={stockFigures}
                        totalCount={totalCount}
                        totalPages={totalPages}
                    />
                    <ProductFiltersDrawer
                        branchScope={branchScope}
                        brandOptions={brandOptions}
                        categoryOptions={categoryOptions}
                        drawer={filtersDrawer}
                    />
                </section>
            </Tabs.Panel>
            <Tabs.Panel className="pt-4" id="barcodeIssues">
                <BarcodeIssues
                    branchId={branchId}
                    isEnabled={!isBranchPending}
                />
            </Tabs.Panel>
        </Tabs>
    );
};
